import { useState } from "react";
import { gsap } from "../../animations/gsapSetup.ts";
import { useGsapScrollTrigger } from "../../hooks/useGsapScrollTrigger.ts";
import { useIsMobile } from "../../hooks/useMediaQuery.ts";
import { useReducedMotion } from "../../hooks/useReducedMotion.ts";
import { useI18n } from "../../i18n/context.tsx";
import styles from "./HrZoneMatrix.module.css";

/* ── Mock FG% data: rows = HR bands, cols = zones ── */
const MATRIX_DATA: number[][] = [
  [92, 68, 42, 25], // Z1 <130
  [88, 65, 40, 22], // Z2 130-145
  [85, 58, 34, 18], // Z3 145-160
  [78, 38, 28, 12], // Z4 160-175
  [72, 22, 20, 8], // Z5 175+
];

type BenchmarkKey = "rec" | "ncaa" | "nba" | "curry" | "simmons";

const BENCHMARKS: Record<BenchmarkKey, { data: number[][] }> = {
  rec: {
    // 社会人バスケ一般 — 週末プレーヤー
    data: [
      [72, 42, 18, 8],
      [68, 38, 15, 5],
      [62, 32, 12, 3],
      [55, 25, 8, 2],
      [45, 18, 5, 1],
    ],
  },
  ncaa: {
    // 全米大学トップ100
    data: [
      [90, 65, 40, 30],
      [87, 62, 38, 27],
      [83, 56, 34, 23],
      [78, 48, 30, 18],
      [72, 38, 25, 14],
    ],
  },
  nba: {
    // NBAプロ平均
    data: [
      [95, 72, 48, 38],
      [93, 70, 46, 36],
      [91, 67, 43, 33],
      [88, 62, 40, 28],
      [84, 55, 36, 24],
    ],
  },
  curry: {
    // ステフィン・カリー — GOAT shooter。ペイントは低め（ガード）、Mid/3PT/Deep異次元
    data: [
      [68, 82, 58, 48],
      [66, 80, 56, 46],
      [64, 78, 54, 44],
      [60, 75, 50, 40],
      [55, 70, 46, 36],
    ],
  },
  simmons: {
    // ベン・シモンズ — ペイント特化、シュート? 何それ? 3PTは存在しない
    data: [
      [88, 32, 5, 0],
      [85, 28, 3, 0],
      [82, 24, 2, 0],
      [78, 18, 1, 0],
      [72, 12, 0, 0],
    ],
  },
};

const WEAKNESS_THRESHOLD = 30; // % decline from Z1 to mark as weakness
const WEAPON_THRESHOLD = 25; // % decline from Z1 — below this = weapon

/* ── Per-zone estimated σ for basketball FG% population ── */
const ZONE_SIGMA: number[] = [14, 15, 13, 12]; // Paint, Mid, 3PT, Deep
const SIGMA_THRESHOLD = 0.5; // ±0.5σ boundary between strong/weak

function getCellColor(pct: number): string {
  if (pct >= 70) return styles.cellGreen;
  if (pct >= 50) return styles.cellYellow;
  if (pct >= 30) return styles.cellOrange;
  return styles.cellRed;
}

function getDiffCellColor(diff: number, colIdx: number): string {
  const zScore = diff / ZONE_SIGMA[colIdx];
  if (zScore >= SIGMA_THRESHOLD) return styles.cellDiffGreen;
  if (zScore >= 0) return styles.cellDiffYellow;
  if (zScore > -SIGMA_THRESHOLD) return styles.cellDiffOrange;
  return styles.cellDiffRed;
}

function getDeclinePercent(z1Value: number, currentValue: number): number {
  if (z1Value === 0) return 0;
  return Math.round(((z1Value - currentValue) / z1Value) * 100);
}

interface CellAnalysis {
  row: number;
  col: number;
  z1Value: number;
  currentValue: number;
  declinePercent: number;
  isWeakness: boolean;
  isWeapon: boolean;
}

function analyzeMatrix(): CellAnalysis[] {
  const results: CellAnalysis[] = [];
  for (let col = 0; col < MATRIX_DATA[0].length; col++) {
    const z1Value = MATRIX_DATA[0][col];
    // Check Zone 4-5 (rows 3-4) for weaknesses/weapons
    for (let row = 3; row < 5; row++) {
      const currentValue = MATRIX_DATA[row][col];
      const decline = getDeclinePercent(z1Value, currentValue);
      results.push({
        row,
        col,
        z1Value,
        currentValue,
        declinePercent: decline,
        isWeakness: decline >= WEAKNESS_THRESHOLD,
        isWeapon: decline < WEAPON_THRESHOLD,
      });
    }
  }
  return results;
}

const CELL_ANALYSIS = analyzeMatrix();

function isWeaknessCell(row: number, col: number): boolean {
  return CELL_ANALYSIS.some((c) => c.row === row && c.col === col && c.isWeakness);
}

function isWeaponCell(row: number, col: number): boolean {
  return CELL_ANALYSIS.some((c) => c.row === row && c.col === col && c.isWeapon);
}

export function HrZoneMatrix() {
  const reducedMotion = useReducedMotion();
  const isMobile = useIsMobile();
  const { t } = useI18n();
  const [benchmark, setBenchmark] = useState<BenchmarkKey | null>(null);

  const ref = useGsapScrollTrigger(
    (el) => {
      if (reducedMotion) return;

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          toggleActions: "play none none none",
        },
      });

      /* ── Header fade in ── */
      const header = el.querySelector(`.${styles.header}`);
      if (header) {
        gsap.set(header, { y: 20, opacity: 0 });
        tl.to(header, { y: 0, opacity: 1, duration: 0.4, ease: "power2.out" }, 0);
      }

      /* ── Matrix cells stagger (top-left to bottom-right) ── */
      if (!isMobile) {
        const cells = el.querySelectorAll(`.${styles.cell}`);
        if (cells.length > 0) {
          gsap.set(cells, { opacity: 0, scale: 0.8 });
          let idx = 0;
          for (const cell of cells) {
            tl.to(
              cell,
              { opacity: 1, scale: 1, duration: 0.2, ease: "power2.out" },
              0.3 + idx * 0.04,
            );
            idx++;
          }
        }
      }

      /* ── Highlight cards y fade-up ── */
      const highlightCards = el.querySelectorAll(`.${styles.highlightCard}`);
      let hIdx = 0;
      for (const card of highlightCards) {
        gsap.set(card, { y: 25, opacity: 0 });
        tl.to(card, { y: 0, opacity: 1, duration: 0.4, ease: "power3.out" }, 0.8 + hIdx * 0.15);
        hIdx++;
      }

      /* ── Badges scale pop ── */
      const badges = el.querySelectorAll(`.${styles.badge}`);
      for (const badge of badges) {
        gsap.set(badge, { scale: 0 });
        tl.to(badge, { scale: 1, duration: 0.25, ease: "back.out(2)" }, 1.0 + hIdx * 0.1);
      }

      /* ── Suggest card fade-up ── */
      const suggestCard = el.querySelector(`.${styles.suggestCard}`);
      if (suggestCard) {
        gsap.set(suggestCard, { y: 30, opacity: 0 });
        tl.to(suggestCard, { y: 0, opacity: 1, duration: 0.4, ease: "power3.out" }, 1.2);
      }
    },
    [isMobile],
  );

  return (
    <div ref={ref} className={styles.wrapper}>
      {/* ── Header ── */}
      <div className={styles.header}>
        <h3 className={styles.sectionTitle}>{t.ai.hrZoneTitle}</h3>
        <p className={styles.sectionDesc}>{t.ai.hrZoneDesc}</p>
      </div>

      {/* ── Headline ── */}
      <div className={styles.headline}>
        <h4 className={styles.headlineText}>{t.ai.hrZoneHeadline}</h4>
        <p className={styles.headlineDesc}>{t.ai.hrZoneHeadlineDesc}</p>
      </div>

      {/* ── Benchmark Toggle ── */}
      <div className={styles.benchmarkRow}>
        {(
          [
            { key: null, label: t.ai.hrZoneBenchmarkYou },
            { key: "rec" as const, label: t.ai.hrZoneBenchmarkRec },
            { key: "ncaa" as const, label: t.ai.hrZoneBenchmarkNcaa },
            { key: "nba" as const, label: t.ai.hrZoneBenchmarkNba },
            { key: "simmons" as const, label: t.ai.hrZoneBenchmarkSimmons },
            { key: "curry" as const, label: t.ai.hrZoneBenchmarkCurry },
          ] as const
        ).map((opt) => (
          <button
            key={opt.key ?? "you"}
            type="button"
            className={`${styles.benchmarkBtn} ${benchmark === opt.key ? styles.benchmarkBtnActive : ""}`}
            onClick={() => setBenchmark(opt.key)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* ── Matrix Table ── */}
      <div className={styles.matrixScroll}>
        <table className={styles.matrix}>
          <thead>
            <tr>
              <th className={styles.cornerCell} />
              {t.ai.hrZoneZones.map((zone) => (
                <th key={zone}>{zone}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {MATRIX_DATA.map((row, rowIdx) => (
              <tr key={t.ai.hrZoneHrBands[rowIdx]}>
                <td className={styles.hrBandLabel}>{t.ai.hrZoneHrBands[rowIdx]}</td>
                {row.map((pct, colIdx) => {
                  const zone = t.ai.hrZoneZones[colIdx];
                  const decline = getDeclinePercent(MATRIX_DATA[0][colIdx], pct);
                  const weakness = isWeaknessCell(rowIdx, colIdx);
                  const weapon = isWeaponCell(rowIdx, colIdx);

                  const benchDiff = benchmark
                    ? pct - BENCHMARKS[benchmark].data[rowIdx][colIdx]
                    : null;
                  const cellColor =
                    benchDiff !== null ? getDiffCellColor(benchDiff, colIdx) : getCellColor(pct);
                  const cellHighlight = !benchmark
                    ? `${weakness ? styles.cellWeakness : ""} ${weapon ? styles.cellWeapon : ""}`
                    : "";

                  return (
                    <td
                      key={`${t.ai.hrZoneHrBands[rowIdx]}-${zone}`}
                      className={`${styles.cell} ${cellColor} ${cellHighlight}`}
                    >
                      {pct}%
                      {benchDiff !== null && benchmark && (
                        <span
                          className={`${styles.diffBadge} ${benchDiff >= 0 ? styles.diffPositive : styles.diffNegative}`}
                        >
                          ({BENCHMARKS[benchmark].data[rowIdx][colIdx]})
                          {benchDiff >= 0 ? `+${benchDiff}` : benchDiff}
                        </span>
                      )}
                      {benchDiff === null && rowIdx > 0 && decline > 0 && (
                        <span className={styles.declineBadge}>-{decline}%</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Weakness & Weapon Highlights ── */}
      <div className={styles.highlights}>
        {/* Weakness card */}
        <div className={`${styles.highlightCard} ${styles.highlightWeakness}`}>
          <div className={styles.highlightHeader}>
            <span className={styles.highlightIcon}>{"🚨"}</span>
            <span className={styles.highlightTitle}>Mid-Range</span>
            <span className={`${styles.badge} ${styles.badgeWeakness}`}>{t.ai.hrZoneWeakness}</span>
          </div>
          <div className={styles.highlightStats}>
            <div className={styles.statRow}>
              <span className={styles.statLabel}>Z1 FG%</span>
              <span className={`${styles.statValue} ${styles.statPositive}`}>68%</span>
            </div>
            <div className={styles.statRow}>
              <span className={styles.statLabel}>Z5 FG%</span>
              <span className={`${styles.statValue} ${styles.statNegative}`}>22%</span>
            </div>
            <div className={styles.statRow}>
              <span className={styles.statLabel}>Decline</span>
              <span className={`${styles.statValue} ${styles.statNegative}`}>-68%</span>
            </div>
          </div>
          <p className={styles.highlightDesc}>{t.ai.hrZoneWeaknessDesc}</p>
        </div>

        {/* Weapon card */}
        <div className={`${styles.highlightCard} ${styles.highlightWeapon}`}>
          <div className={styles.highlightHeader}>
            <span className={styles.highlightIcon}>{"💪"}</span>
            <span className={styles.highlightTitle}>Paint</span>
            <span className={`${styles.badge} ${styles.badgeWeapon}`}>{t.ai.hrZoneWeapon}</span>
          </div>
          <div className={styles.highlightStats}>
            <div className={styles.statRow}>
              <span className={styles.statLabel}>Z1 FG%</span>
              <span className={`${styles.statValue} ${styles.statPositive}`}>92%</span>
            </div>
            <div className={styles.statRow}>
              <span className={styles.statLabel}>Z5 FG%</span>
              <span className={`${styles.statValue} ${styles.statPositive}`}>72%</span>
            </div>
            <div className={styles.statRow}>
              <span className={styles.statLabel}>Decline</span>
              <span className={`${styles.statValue} ${styles.statPositive}`}>-22%</span>
            </div>
          </div>
          <p className={styles.highlightDesc}>{t.ai.hrZoneWeaponDesc}</p>
        </div>
      </div>

      {/* ── AI Suggest Card ── */}
      <div className={styles.suggestCard}>
        <div className={styles.suggestHeader}>
          <span className={styles.suggestIcon}>{"🧠"}</span>
          <span className={styles.suggestTitle}>{t.ai.hrZoneSuggestTitle}</span>
        </div>
        <div className={styles.suggestItems}>
          <div className={styles.suggestItem}>
            <span className={`${styles.suggestDot} ${styles.suggestDotRed}`} />
            <p className={styles.suggestText}>{t.ai.hrZoneWeaknessDesc}</p>
          </div>
          <div className={styles.suggestItem}>
            <span className={`${styles.suggestDot} ${styles.suggestDotGreen}`} />
            <p className={styles.suggestText}>{t.ai.hrZoneWeaponDesc}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
